import {
  clampMenuToViewport,
  ContextMenuItem,
  ContextMenuSurface,
  KebabButton,
} from '@src/components/common/contextMenuStyles';
import { CLIP_PREVIEW_Z } from '@src/components/content-components/clipPreviewZ';
import { useOutsideAlerter } from '@src/components/transcript/useClickOutside';
import DeleteIcon from '@src/svgs/DeleteIcon';
import MoreVertIcon from '@src/svgs/MoreVertIcon';
import SimplePencilIcon from '@src/svgs/SimplePencilIcon';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

interface SavedSegmentRowActionsMenuProps {
  /** Shown in the aria-label so each row's trigger is distinguishable. */
  segmentName: string;
  onRename: () => void;
  onDelete: () => void;
}

/** Rough surface size for viewport clamping before the menu is measured. */
const MENU_WIDTH = 200;
const MENU_HEIGHT = 90;

/**
 * Per-highlight "more options" kebab inside the Highlights dropdown
 * (PLAT-11182): Rename Highlight / Delete Highlight. Combining is deliberately
 * absent — it needs the user to have narrowed to one highlight, so it is
 * offered as an inline button on that row instead of as a menu item that would
 * be live in states where it cannot apply.
 *
 * It opens a body-portaled menu from *inside* another body-portaled,
 * `useAnchoredDropdown`-driven menu, which takes two non-obvious guards —
 * both learned the hard way in `MusicActionsMenu` / `DubActionsMenu`
 * (PLAT-10627 / PLAT-10732):
 *
 * 1. The parent dropdown closes on any document `mousedown` outside its own
 *    ref. This menu portals to `<body>`, i.e. outside it, so a click here
 *    would dismiss the parent and unmount this menu *before* the `click`
 *    fires — the item's handler would silently never run. Hence
 *    `onMouseDown` stops propagation on the surface.
 * 2. `ContextMenuSurface`'s resting `z-index` (100001) is an order of
 *    magnitude BELOW the parent dropdown's 1000011, so without an explicit
 *    lift the menu opens *behind* the list it belongs to.
 */
const SavedSegmentRowActionsMenu: React.FC<SavedSegmentRowActionsMenuProps> = ({
  segmentName,
  onRename,
  onDelete,
}) => {
  const [position, setPosition] = useState<{ x: number; y: number } | null>(
    null,
  );
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => setPosition(null), []);

  // Ignore outside-clicks that land on the kebab itself — its own onClick
  // toggles the menu, so letting this fire too would race it.
  const handleOutside = useCallback(
    (event: MouseEvent) => {
      const target = event.target;
      if (target instanceof Node && triggerRef.current?.contains(target)) {
        return;
      }
      close();
    },
    [close],
  );
  useOutsideAlerter(menuRef, handleOutside);

  // Escape on CAPTURE: the parent dropdown listens on bubble and stops
  // propagation there, so a bubble listener here would lose the race and the
  // parent would close instead of just this menu.
  useEffect(() => {
    if (!position) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      close();
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [position, close]);

  const toggle = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    if (position) {
      close();
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    setPosition(
      clampMenuToViewport(rect.right, rect.bottom + 4, MENU_WIDTH, MENU_HEIGHT),
    );
  };

  // Every item closes this menu first, then runs. The parent dropdown stays
  // open: rename swaps the row into an input there, and delete raises its
  // confirmation over it.
  const run = (action: () => void) => (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    close();
    action();
  };

  return (
    <>
      <RowKebabButton
        ref={triggerRef}
        type="button"
        onClick={toggle}
        aria-label={`More options for ${segmentName}`}
        aria-haspopup="menu"
        aria-expanded={!!position}
        data-testid="saved-segment-row-actions-trigger"
      >
        <MoreVertIcon width="14" height="14" />
      </RowKebabButton>
      {position &&
        createPortal(
          <ContextMenuSurface
            ref={menuRef}
            role="menu"
            onMouseDown={event => event.stopPropagation()}
            style={{
              top: position.y,
              left: position.x,
              zIndex: CLIP_PREVIEW_Z.languageDropdownPopover,
            }}
            data-testid="saved-segment-row-actions-menu"
          >
            <ContextMenuItem
              role="menuitem"
              onClick={run(onRename)}
              data-testid="saved-segment-row-rename"
            >
              <SimplePencilIcon />
              Rename Highlight
            </ContextMenuItem>
            <ContextMenuItem
              $danger
              role="menuitem"
              onClick={run(onDelete)}
              data-testid="saved-segment-row-delete"
            >
              <DeleteIcon />
              Delete Highlight
            </ContextMenuItem>
          </ContextMenuSurface>,
          document.body,
        )}
    </>
  );
};

export default SavedSegmentRowActionsMenu;

/* The shared KebabButton's left margin is meant for wide list rows; inside a
   280px menu row it pushes the trigger off the edge, and the row already
   owns its gap. */
const RowKebabButton = styled(KebabButton)`
  margin-left: 0;
  padding: 2px;
  flex-shrink: 0;
`;
